import React, { useState, useCallback, useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import Popup from '@atlaskit/popup';
import Select, { type ActionMeta, AsyncSelect } from '@atlaskit/select';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { StatusLozenge } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/status/status-lozenge/index.tsx';
import type { StatusPickerOption } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/picker-options.tsx';
import { ControlComponent } from '../common/ui/control/index.tsx';
import { DropdownIndicator } from '../common/ui/dropdown-indicator/index.tsx';
import PopupComponent from '../common/ui/popup-container/index.tsx';
import statusMessages from '../common/ui/status/messages.tsx';
import { useStatusesSearch } from '../services/index.tsx';
import statusPickerMessages from './messages.tsx';
import { transformStatusToSelectOption } from './utils.tsx';

type Props = {
	isEditable: boolean;
	hasValidationError?: boolean;
	value: StatusPickerOption | null;
	objectSchemaId: string;
	objectTypeMultiValues: string[] | undefined;
	autoFocus?: boolean;
	menuIsOpen?: boolean;
	onChange?: (options: StatusPickerOption[]) => void;
	isDisabled?: boolean;
	fieldId?: string;
};

type GroupedStatusPickerOption = {
	label: string;
	options: StatusPickerOption[];
};

export const POPUP_SELECT_ZINDEX = layers.modal + 1;

const MAX_MENU_HEIGHT = 300;
// Minus control minus padding
const MAX_MENU_LIST_HEIGHT = MAX_MENU_HEIGHT - 60;

const REMOVE_ACTION_META_TYPES = ['clear', 'remove-value', 'pop-value'];

const formatOptionLabel = ({ value, category, label }: StatusPickerOption) => (
	<StatusLozenge isEditable={false} ellipsis status={{ id: value, name: label, category }} />
);

export const StatusPicker = ({
	onChange = noop,
	objectSchemaId,
	value,
	objectTypeMultiValues,
	hasValidationError,
	menuIsOpen = false,
	isDisabled,
	fieldId,
}: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const popupTriggerRef = useRef<HTMLDivElement>(null);
	const [defaultOptions, setDefaultOptions] = useState<GroupedStatusPickerOption[] | null>(null);
	const [popupOpen, setPopupOpen] = useState<boolean>(menuIsOpen);
	const [closeToBottom, setCloseToBottom] = useState<boolean>(false);
	const { error, loading, fetch: searchStatuses } = useStatusesSearch();

	/*  Infer the menu position for asynchronously loaded options. Popup update doesn't work when called after
        fetching options and appears below select causing an issue when the select is at the bottom of the window */
	useEffect(() => {
		if (popupTriggerRef.current) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const distance = window.innerHeight - popupTriggerRef.current.getBoundingClientRect().bottom;
			setCloseToBottom(distance < MAX_MENU_HEIGHT);
		}
	}, [popupOpen]);

	const onSearchStatuses = useCallback(
		(searchQuery?: string, onStatusesLoaded?: (arg1: GroupedStatusPickerOption[]) => void) => {
			searchStatuses({
				objectSchemaId,
				objectTypeMultiValues,
				query: searchQuery ?? '',
			}).then((response) => {
				if (response?.statuses) {
					const statusListOptions = response.statuses.map(transformStatusToSelectOption);
					const groupedOptions: GroupedStatusPickerOption = {
						label: formatMessage(statusPickerMessages.groupHeader, {
							numberOfStatuses: response.statuses.length,
							totalStatuses: response.total,
						}),
						options: statusListOptions,
					};
					onStatusesLoaded?.([groupedOptions]);
				}
			});
		},
		[formatMessage, objectSchemaId, objectTypeMultiValues, searchStatuses],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(
		(
			query: string | undefined,
			callback?: (arg1: GroupedStatusPickerOption[] | undefined) => void,
		) => onSearchStatuses(query, callback),
		400,
	);

	const onStatusSelected = useCallback(
		(newValues: StatusPickerOption | null, meta?: ActionMeta<StatusPickerOption>) => {
			setPopupOpen(false);
			if (meta?.action && meta?.action && REMOVE_ACTION_META_TYPES.includes(meta.action)) {
				// When select is cleared set status to None
				onChange([
					{
						label: formatMessage(statusMessages.noValue),
						value: '',
						category: null,
					},
				]);
			}
			if (newValues) {
				onChange([newValues]);
			}
		},
		[formatMessage, onChange],
	);

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				title: statusPickerMessages.errorFlagTitle,
				description: statusPickerMessages.errorFlagDescription,
			});
		}
	}, [error, showFlag]);

	const validationState = hasValidationError === true ? 'error' : 'default';

	const togglePopupOpen = useCallback(() => {
		setPopupOpen((prevState) => !prevState);
	}, []);

	// We need this due to calling close when inline editing is a noop due to component unmount
	const closePopup = useCallback(() => {
		menuIsOpen ? undefined : setPopupOpen(false);
	}, [menuIsOpen]);

	return (
		<Popup
			testId="servicedesk-insight-status-picker.ui.popup"
			isOpen={popupOpen}
			autoFocus={false}
			placement={closeToBottom ? 'top-start' : 'bottom-start'}
			zIndex={POPUP_SELECT_ZINDEX}
			popupComponent={PopupComponent}
			onClose={closePopup}
			trigger={(triggerProps) => (
				// This is basically a dummy display component for the value and handles opening the select
				<div {...triggerProps}>
					{/* triggerProps already has it's own ref so unforunately need an additional div */}
					<div ref={popupTriggerRef}>
						<Select
							isDisabled={isDisabled}
							inputId={fieldId}
							classNamePrefix="insight-object-attribute-status-picker-value"
							value={value}
							onChange={onStatusSelected}
							isClearable
							validationState={validationState}
							components={{ Menu: () => null, MenuList: () => null }}
							formatOptionLabel={formatOptionLabel}
							onMenuOpen={togglePopupOpen}
							isSearchable={false}
						/>
					</div>
				</div>
			)}
			content={() => (
				// This is the search component to asynchronously call the API
				<AsyncSelect
					// Used for tests
					classNamePrefix="insight-object-attribute-status-picker"
					value={value}
					onChange={onStatusSelected}
					placeholder={formatMessage(statusPickerMessages.searchPlaceholder)}
					controlShouldRenderValue={false}
					isClearable={false}
					backspaceRemovesValue={false}
					hideSelectedOptions={false}
					tabSelectsValue={false}
					components={{
						DropdownIndicator,
						Control: ControlComponent,
					}}
					defaultOptions={defaultOptions}
					formatOptionLabel={formatOptionLabel}
					isLoading={loading}
					loadOptions={debouncedLoadOptions}
					autoFocus
					menuIsOpen={popupOpen}
					onFocus={() => {
						if (defaultOptions === null) {
							onSearchStatuses('', setDefaultOptions);
						}
					}}
					maxMenuHeight={MAX_MENU_LIST_HEIGHT}
					styles={{
						menu: (base) => ({
							...base,
							width: 'max-content',
							minWidth: '100%',
							position: 'relative',
							boxShadow: 'none',
							marginTop: 0,
							marginBottom: 0,
						}),
						placeholder: (base) => ({
							...base,
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
							width: '100%',
							overflow: 'hidden',
						}),
					}}
				/>
			)}
		/>
	);
};
